import axiosInstance from './axiosInstance'

// ── Enums & Constants ─────────────────────────────────────────────────────────

export const CHANNEL_TYPES = ['META', 'WEBSITE', 'WHATSAPP', 'REST_API', 'INSTAGRAM', 'GOOGLE'] as const
export type ChannelType = typeof CHANNEL_TYPES[number]

export const CHANNEL_STATUSES = ['ACTIVE', 'INACTIVE'] as const
export type ChannelStatus = typeof CHANNEL_STATUSES[number]

export const SYNC_STATUSES = ['PROCESSING', 'SUCCESS', 'FAILED'] as const
export type SyncStatus = typeof SYNC_STATUSES[number]

export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  META:      'Meta / Facebook',
  WEBSITE:   'Website Form',
  WHATSAPP:  'WhatsApp',
  REST_API:  'REST API',
  INSTAGRAM: 'Instagram',
  GOOGLE:    'Google Ads',
}

export const CHANNEL_TYPE_COLORS: Record<ChannelType, string> = {
  META:      '#1877F2',
  WEBSITE:   '#10b981',
  WHATSAPP:  '#25D366',
  REST_API:  '#6366f1',
  INSTAGRAM: '#E1306C',
  GOOGLE:    '#EA4335',
}

export interface CrmLeadField {
  value:    string
  label:    string
  required: boolean
  dbColumn: string
}

/**
 * CRM lead fields available for mapping — values match the actual DB column
 * names in public.leads so the backend can write them directly.
 *
 * System-managed columns (Id, OwnerId, Status, CreatedAt, UpdatedAt,
 * ConvertedAt, is_deleted, etc.) are intentionally excluded — they are
 * never written from an incoming webhook payload.
 */
export const CRM_LEAD_FIELDS: CrmLeadField[] = [
  // Core identity
  { value: 'Name',              label: 'Full Name',            required: true,  dbColumn: 'Name' },
  { value: 'Phone',             label: 'Phone',                required: true,  dbColumn: 'Phone' },
  { value: 'Email',             label: 'Email',                required: false, dbColumn: 'Email' },
  // Lead metadata
  { value: 'Source',            label: 'Lead Source',          required: false, dbColumn: 'Source' },
  { value: 'BestTimeToContact', label: 'Best Time to Contact', required: false, dbColumn: 'BestTimeToContact' },
  // "Description" in the lead form is stored in the NextAction column
  { value: 'NextAction',        label: 'Description',          required: false, dbColumn: 'NextAction' },
  // Company / address
  { value: 'company_name',      label: 'Company Name',         required: false, dbColumn: 'company_name' },
  { value: 'address',           label: 'Address',              required: false, dbColumn: 'address' },
  { value: 'pincode',           label: 'Pincode',              required: false, dbColumn: 'pincode' },
  { value: 'gst',               label: 'GST Number',           required: false, dbColumn: 'gst' },
  // Tenant context — Organisation & LOB are intentionally NOT mappable:
  // they are fixed at channel creation (Integration Scope) and applied to
  // every synced lead automatically.
  { value: 'warehouse_id',      label: 'Warehouse',            required: false, dbColumn: 'warehouse_id' },
  { value: 'customer_type_id',  label: 'Customer Type',        required: false, dbColumn: 'customer_type_id' },
  { value: 'file_type',         label: 'File Type',            required: false, dbColumn: 'file_type' },
]

export type CrmLeadFieldValue = string

/** DB column names that MUST be mapped for a lead to be created */
export const REQUIRED_CRM_FIELDS: string[] = ['Name', 'Phone']

// ── Shared types ──────────────────────────────────────────────────────────────

export interface AuditUser {
  id:        string
  name:      string
  email:     string
  avatarUrl: string | null
}

// ── Field Mapping ─────────────────────────────────────────────────────────────

export interface FieldMapping {
  platformField: string   // field name from the incoming webhook/API payload
  crmField:      string   // CRM lead field it maps to
  transform?:    string   // optional: 'uppercase' | 'lowercase' | 'trim'
  staticValue?:  string | null  // fixed value chosen by the user (e.g. customer type id) — ignores the payload
}

// ── Auto Assign Rule ──────────────────────────────────────────────────────────

export interface AutoAssignRule {
  id:           string
  assignedToId: string
  assignedTo:   AuditUser | null
  priority:     number            // lower = higher priority
  condition?:   string            // e.g. 'source=FACEBOOK'
}

// ── Error Log ─────────────────────────────────────────────────────────────────

export interface ChannelErrorLog {
  id:        string
  channelId: string
  message:   string
  payload?:  Record<string, unknown>
  createdAt: string
}

// ── Lead Source ───────────────────────────────────────────────────────────────

export interface LeadSource {
  id:           string
  name:         string
  /** Optional — sources created standalone from Settings → Lead Sources carry no channel. */
  channelId:    string | null
  channelName?: string | null
  channelType?: ChannelType | null
  /** Org scoping: null = global source, visible to every organisation. */
  orgId?:       string | null
  orgName?:     string | null
  channel?:     { id: string; name: string; type: ChannelType }
  createdAt:    string
}

export interface CreateLeadSourcePayload {
  name:       string
  channelId?: string
  /** SuperAdmin only: org this source belongs to (omit = global source). */
  orgId?:     string
}

// ── Channel ───────────────────────────────────────────────────────────────────

export interface ChannelSyncLog {
  id:             string
  channelId:      string
  status:         SyncStatus
  startedAt:      string
  completedAt:    string | null
  leadsFetched:   number
  leadsImported:  number
  leadsDuplicate: number
  leadsFailed:    number
  syncFrom:       string | null
  syncTo:         string | null
  errorMessage:   string | null
  createdAt:      string
}

export interface Channel {
  id:                   string
  name:                 string
  type:                 ChannelType
  status:               ChannelStatus
  accessToken:          string | null
  refreshToken:         string | null
  webhookSecret:        string | null
  webhookUrl:           string | null
  apiBaseUrl:           string | null
  syncFrequencyMinutes: number
  fieldMappings:        FieldMapping[]
  autoAssignRules:      AutoAssignRule[]
  leadSources:          LeadSource[]
  lastSyncAt:           string | null
  lastSuccessfulSyncAt: string | null
  lastSyncStatus:       SyncStatus | null
  lastSyncError:        string | null
  organizationId:       string | null
  organizationName:     string | null
  lobId:                string | null
  lobName:              string | null
  createdBy:            AuditUser | null
  createdAt:            string
  updatedAt:            string
  errorCount:           number
}

export interface PaginatedChannels {
  items:      Channel[]
  total:      number
  page:       number
  limit:      number
  totalPages: number
}

export interface ChannelFilters {
  search?:   string
  types?:    ChannelType[]
  statuses?: ChannelStatus[]
  orgIds?:   string[]
  page?:     number
  pageSize?: number
}

export interface CreateChannelPayload {
  name:                  string
  type:                  ChannelType
  accessToken?:          string
  refreshToken?:         string
  webhookSecret?:        string
  apiBaseUrl?:           string
  syncFrequencyMinutes?: number
  fieldMappings?:        FieldMapping[]
  orgId?:                string
  lobId?:                string
}

export interface UpdateChannelPayload {
  name?:                 string
  type?:                 ChannelType
  status?:               ChannelStatus
  accessToken?:          string | null
  refreshToken?:         string | null
  webhookSecret?:        string | null
  apiBaseUrl?:           string | null
  syncFrequencyMinutes?: number
  fieldMappings?:        FieldMapping[]
}

// ── Queue / Sync types (sync-service API on port 4001) ────────────────────────

export interface QueueStats {
  waiting:   number
  active:    number
  completed: number
  failed:    number
  delayed:   number
  queueName: string
}

export interface QueueJob {
  id:           string
  name:         string
  data:         unknown
  failedReason: string | null
  attemptsMade: number
  processedOn:  number | null
  finishedOn:   number | null
  returnvalue?: unknown
}

// ═════════════════════════════════════════════════════════════════════════════
// Channels API
// ═════════════════════════════════════════════════════════════════════════════

export const channelsApi = {

  // ── Channel CRUD ─────────────────────────────────────────────────────────────

  getAll: async (filters: ChannelFilters = {}): Promise<PaginatedChannels> => {
    const res = await axiosInstance.get<{ data: any }>('/channels', { params: filters })
    const raw = res.data.data
    return {
      items:      raw.items      ?? raw.results     ?? [],
      total:      raw.total      ?? raw.rowCount     ?? 0,
      page:       raw.page       ?? raw.currentPage  ?? 1,
      limit:      raw.limit      ?? raw.pageSize     ?? 20,
      totalPages: raw.totalPages ?? (raw.pageSize > 0
        ? Math.ceil((raw.rowCount ?? 0) / raw.pageSize) : 1),
    }
  },

  getById: async (id: string): Promise<Channel> => {
    const res = await axiosInstance.get<{ data: Channel }>(`/channels/${id}`)
    return res.data.data
  },

  create: async (payload: CreateChannelPayload): Promise<Channel> => {
    const res = await axiosInstance.post<{ data: Channel }>('/channels', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateChannelPayload): Promise<Channel> => {
    const res = await axiosInstance.put<{ data: Channel }>(`/channels/${id}`, payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/channels/${id}`)
  },

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  toggleStatus: async (id: string, status: ChannelStatus): Promise<Channel> => {
    const res = await axiosInstance.patch<{ data: Channel }>(`/channels/${id}/status`, { status })
    return res.data.data
  },

  /** Manually trigger a sync for the channel — returns immediately with syncLogId; actual sync runs in background */
  sync: async (id: string): Promise<{ syncLogId: string }> => {
    const res = await axiosInstance.post<{ data: { syncLogId: string } }>(`/channels/${id}/sync`)
    return res.data.data
  },

  // ── Field Mappings ───────────────────────────────────────────────────────────

  updateFieldMappings: async (id: string, mappings: FieldMapping[]): Promise<Channel> => {
    const res = await axiosInstance.put<{ data: Channel }>(`/channels/${id}/field-mappings`, { mappings })
    return res.data.data
  },

  // ── Auto Assign Rules ────────────────────────────────────────────────────────

  getAutoAssignRules: async (id: string): Promise<AutoAssignRule[]> => {
    const res = await axiosInstance.get<{ data: AutoAssignRule[] }>(`/channels/${id}/auto-assign-rules`)
    return res.data.data
  },

  createAutoAssignRule: async (id: string, rule: { assignedToId: string; priority?: number; condition?: string }): Promise<AutoAssignRule> => {
    const res = await axiosInstance.post<{ data: AutoAssignRule }>(`/channels/${id}/auto-assign-rules`, rule)
    return res.data.data
  },

  deleteAutoAssignRule: async (channelId: string, ruleId: string): Promise<void> => {
    await axiosInstance.delete(`/channels/${channelId}/auto-assign-rules/${ruleId}`)
  },

  // ── Error Logs ───────────────────────────────────────────────────────────────

  getErrorLogs: async (id: string): Promise<ChannelErrorLog[]> => {
    const res = await axiosInstance.get<{ data: ChannelErrorLog[] }>(`/channels/${id}/error-logs`)
    return res.data.data
  },

  clearErrorLogs: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/channels/${id}/error-logs`)
  },

  // ── Webhook ──────────────────────────────────────────────────────────────────

  /** Regenerate the webhook secret (and thus the webhook URL) */
  rotateWebhookSecret: async (id: string): Promise<{ webhookSecret: string; webhookUrl: string }> => {
    const res = await axiosInstance.post<{ data: { webhookSecret: string; webhookUrl: string } }>(`/channels/${id}/rotate-webhook`)
    return res.data.data
  },
}

// ═════════════════════════════════════════════════════════════════════════════
// Lead Sources API
// ═════════════════════════════════════════════════════════════════════════════

export const leadSourcesApi = {

  getAll: async (channelId?: string): Promise<LeadSource[]> => {
    const res = await axiosInstance.get<{ data: LeadSource[] }>('/lead-sources', {
      params: channelId ? { channelId } : undefined,
    })
    return res.data.data
  },

  create: async (payload: CreateLeadSourcePayload): Promise<LeadSource> => {
    const res = await axiosInstance.post<{ data: LeadSource }>('/lead-sources', payload)
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/lead-sources/${id}`)
  },
}

// ═════════════════════════════════════════════════════════════════════════════
// Sync Logs API  (proxied through .NET backend)
// ═════════════════════════════════════════════════════════════════════════════

export const syncLogsApi = {
  getByChannel: async (channelId: string): Promise<ChannelSyncLog[]> => {
    const res = await axiosInstance.get<{ data: ChannelSyncLog[] }>(`/sync/logs/${channelId}`)
    return res.data.data
  },
}

// ═════════════════════════════════════════════════════════════════════════════
// Queue API  (calls sync-service on port 4001 via /sync proxy)
// ═════════════════════════════════════════════════════════════════════════════

import axios from 'axios'

const SYNC_BASE = '/sync/api/queue'

function syncApi() {
  return axios.create({ baseURL: SYNC_BASE, timeout: 10_000 })
}

export const queueApi = {

  getStats: async (): Promise<QueueStats> => {
    const res = await syncApi().get<{ success: boolean; data: QueueStats }>('/stats')
    return res.data.data
  },

  getFailed: async (limit = 50): Promise<QueueJob[]> => {
    const res = await syncApi().get<{ success: boolean; data: QueueJob[] }>(`/failed?limit=${limit}`)
    return res.data.data
  },

  getCompleted: async (limit = 50): Promise<QueueJob[]> => {
    const res = await syncApi().get<{ success: boolean; data: QueueJob[] }>(`/completed?limit=${limit}`)
    return res.data.data
  },

  getActive: async (): Promise<QueueJob[]> => {
    const res = await syncApi().get<{ success: boolean; data: QueueJob[] }>('/active')
    return res.data.data
  },

  retryJob: async (jobId: string): Promise<void> => {
    await syncApi().post(`/retry/${jobId}`)
  },

  removeJob: async (jobId: string): Promise<void> => {
    await syncApi().delete(`/failed/${jobId}`)
  },

  triggerSync: async (): Promise<{ message: string }> => {
    const res = await syncApi().post<{ success: boolean; message: string }>('/trigger-sync')
    return { message: res.data.message }
  },

  testConnection: async (apiBaseUrl: string, accessToken: string): Promise<{ ok: boolean; message: string; keys?: string[] }> => {
    const res = await axiosInstance.post<{ data: { ok: boolean; message: string; keys?: string[] } }>('/sync/test-connection', {
      apiBaseUrl, accessToken,
    })
    return { ok: res.data.data.ok, message: res.data.data.message, keys: res.data.data.keys }
  },
}
